<script setup lang="ts">
import type { LocationQueryRaw } from "vue-router";
import type { AssetManagementAccessGroup } from "~/graphql/generated/graphql";

const props = defineProps<{
  showAccesGroups: boolean;
  accessGroups?: AssetManagementAccessGroup[];
  currentAccessGroup?: AssetManagementAccessGroup;
}>();

const emit = defineEmits<{ (event: "navigate-to", name: string, query?: LocationQueryRaw): void }>();

const t = useTranslator();
const menuItems = useChromeMenuItems();
const accessGroupStore = useAccessGroupStore();

const searchVisible = ref(false);

const accessGroupName = computed(() => {
  const accessGroupId = accessGroupStore.accessGroup || props.currentAccessGroup?.id;

  return props.accessGroups?.find((ag) => ag.id === accessGroupId)?.name || "";
});

const hasChildren = (item: SideMenuItem) => item.children && item.children.length > 0;

const toggleSearch = () => {
  searchVisible.value = !searchVisible.value;
};

const menuClick = (item: SideMenuItem, event?: MouseEvent) => {
  item.onClick && item.onClick();

  // Special case for search
  if (item.type == MenuItemType.SEARCH) {
    toggleSearch();

    // Prevent the click event from propagating which would close the search again
    if (event) event.stopPropagation();
  }
};

const itemSorted = (items: SideMenuItem[]) => items.sort((a, b) => a.position - b.position);
</script>

<template>
  <div :class="$style.container">
    <ModalChromeSearch :open="searchVisible" @close="() => (searchVisible = false)" />

    <div :class="$style.overflow">
      <div :class="$style.logo"> <ChromeLogo @click="() => emit('navigate-to', 'index')" /> </div>

      <div :class="$style.navigationControls" v-if="!menuItems.loading.value">
        <template v-for="(m, i) in itemSorted(menuItems.homeItems)" :key="i">
          <ExpandableMenuItem v-if="hasChildren(m)" :item="m" :expandsOnItemClick="m.route == undefined" @click="() => menuClick(m)">
            <template #container>
              <SideMenuItem v-for="(c, j) in m.children" :key="j" :indentationLevel="1" :item="m" @click="() => menuClick(c)" />
            </template>
          </ExpandableMenuItem>

          <SideMenuItem :item="m" @click="(e: MouseEvent) => menuClick(m, e)" />
        </template>
      </div>

      <CompositionSpacer direction="vertical" size="medium" />

      <KeyValuePair direction="vertical" spacingSize="none">
        <template #containerStart>
          <Copy type="body" size="small" color="--color-accent-500" style="margin-left: 0.5rem">{{ t("SIDE_MENU_MODULES") }} </Copy>
        </template>

        <template #containerEnd>
          <div :class="$style.navigationControls" v-if="!menuItems.loading.value">
            <template v-for="(m, i) in itemSorted(menuItems.moduleItems.value)" :key="i">
              <ExpandableMenuItem v-if="hasChildren(m)" :item="m" :expandsOnItemClick="m.route == undefined" @click="() => menuClick(m)">
                <template #container>
                  <SideMenuItem v-for="(c, j) in m.children" :key="j" :item="c" :indentationLevel="1" @click="() => menuClick(c)" />
                </template>
              </ExpandableMenuItem>

              <SideMenuItem v-else :item="m" @click="() => menuClick(m)" />
            </template>
          </div>
        </template>
      </KeyValuePair>

      <div style="flex: 1" />

      <KeyValuePair direction="vertical" spacingSize="none">
        <template #containerStart>
          <Copy type="body" size="small" color="--color-accent-500" style="margin-left: 0.5rem"> {{ t("SIDE_MENU_SETTINGS") }} </Copy>
        </template>

        <template #containerEnd>
          <div :class="$style.navigationControls" v-if="!menuItems.loading.value">
            <template v-for="(m, i) in itemSorted(menuItems.settingsItems)" :key="i">
              <ExpandableMenuItem v-if="hasChildren(m)" :item="m" :expandsOnItemClick="m.route == undefined" @click="() => menuClick(m)">
                <template #container>
                  <SideMenuItem v-for="(c, j) in m.children" :key="j" :item="c" :indentationLevel="1" @click="() => menuClick(c)" />
                </template>
              </ExpandableMenuItem>

              <SideMenuItem v-else :item="m" @click="() => menuClick(m)" />
            </template>
          </div>
        </template>
      </KeyValuePair>

      <CompositionSpacer v-if="props.showAccesGroups" direction="vertical" size="small" />

      <div :class="$style.accessGroup" v-if="props.showAccesGroups">
        <KeyValuePair direction="vertical">
          <template #containerStart>
            <Copy type="body" size="small" color="--color-accent-500"> {{ t("SIDE_MENU_ACCESS_GROUP") }} </Copy>
          </template>

          <template #containerEnd>
            <DropDown
              v-model="accessGroupStore.accessGroup"
              :label="accessGroupName"
              colorForeground="--color-accent-300"
              colorBackground="--color-accent-950"
              colorBorder="--color-accent-900"
            >
              <DropDownItem v-for="ag in accessGroups" :key="ag.id" :value="ag.id" :label="ag.name" />
            </DropDown>
          </template>
        </KeyValuePair>
      </div>
    </div>
  </div>
</template>

<style module>
.container {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  padding: 1rem 0;
  background: var(--color-menu-background);
}

.overflow {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow-y: auto;
}

.logo {
  display: flex;
  margin: 0.5rem 1rem;
}

.search {
  display: flex;
  justify-content: center;
  margin: 1rem 0.5rem;
}

.navigationControls {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 0.25rem;
  cursor: pointer;
}

.accessGroup {
  padding: 0 0.5rem;
}
</style>
